import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useUiContext from '@/custolink-hooks/useUiContext'
import RdaLoading from '@/custolink-views/asset_frontend/redesigns/RdaLoading'
import RdaDrawer from '@/custolink-views/asset_frontend/redesigns/RdaDrawer'
import useAssetViewFacade from '@/custolink-views/asset_frontend/asset_view/useAssetViewFacade'
import {
  PublicAssetFrontendApi$AssetComponentDtoKey,
  type PublicAssetFrontendApi$AssetComponentDto,
} from '@/custolink-transpiled_by_server/bc.asset_interaction.api_methods'
import { visitAsset } from '@/custolink-views/asset_frontend/visited_assets'
import RegisterProduct from '@/custolink-views/asset_frontend/asset_view/asset_components/RegisterProduct'
import Inquiry from '@/custolink-views/asset_frontend/asset_view/asset_components/Inquiry'
import AiSupportComponent from '@/custolink-views/asset_frontend/asset_view/asset_components/ai_support/AiSupportComponent'
import FileComponent from '@/custolink-views/asset_frontend/asset_view/asset_components/file_component/FileComponent'
import VideoCallAppPageComponent from '@/custolink-views/asset_frontend/asset_view/asset_components/VideoCallAppPageComponent'
import VoiceCallAppPageComponent from '@/custolink-views/asset_frontend/asset_view/asset_components/VoiceCallAppPageComponent'
import NudgeSerialSheet from '@/custolink-views/asset_frontend/asset_view/NudgeSerialSheet'
import AccountDrawer from '@/custolink-views/asset_frontend/account/AccountDrawer'
import AssetProtectedSignIn from '@/custolink-views/asset_frontend/asset_view/AssetProtectedSignIn'
import Scan from '@/custolink-views/asset_frontend/scan/Scan'
import {
  AssetShell,
  PreviewHeader,
  PreviewNav,
  ResourceRow,
  RowGroup,
  SectionHeading,
  SupportCard,
} from '@/custolink-views/asset_frontend/redesigns/tileKit'
import AssetSearch from '@/custolink-views/asset_frontend/redesigns/AssetSearch'
import { layoutComponents } from '@/custolink-views/asset_frontend/redesigns/componentLayout'
import { usePreviewFonts } from '@/custolink-views/asset_frontend/redesigns/fonts'
import useVoiceCallMode from '@/custolink-views/asset_frontend/redesigns/useVoiceCallMode'
import AssetDrawerScope from '@/custolink-views/asset_frontend/redesigns/AssetDrawerScope'
import { normaliseHex } from '@/custolink-views/asset_frontend/redesigns/theme'
import type { AssetViewProps } from '@/custolink-views/asset_frontend/asset_view/AssetViewClassic'

const KEY = PublicAssetFrontendApi$AssetComponentDtoKey

/**
 * The asset detail page, enriched after the reference mockup: the current asset
 * as an identity card, the support actions as a card grid, and the file
 * components as a documentation list, all in the design-2 language. Only server
 * data is shown - component names, product name and serial; no invented
 * descriptions or status.
 *
 * Same facade, same drawers, same calls. The instant `tel:` voice path and the
 * "mark as visited" / nudge behaviour are preserved.
 */
export default function AssetViewDesign(props: AssetViewProps) {
  usePreviewFonts(['schibsted'])
  const uic = useUiContext()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const facade = useAssetViewFacade(
    props.serialNumber,
    props.customAssetView.urlSlug,
    props.customAssetView.assetAttrValue
  )
  const [markAsVisited, setMarkAsVisited] = useState(0)
  const [nudgeDecision, setNudgeDecision] = useState<boolean | null>(null)
  // A question typed in the search and handed to the AI chat; cleared on close.
  const [aiQuestion, setAiQuestion] = useState<string | undefined>(undefined)

  const details = facade.details
  const { all, support, actions, docs } = layoutComponents(details?.components)
  const aiSupport = all.find((c) => c.key === KEY.AiSupport) ?? null
  const voiceComponent = all.find((c) => c.key === KEY.VoiceCall) ?? null
  const voiceCall = useVoiceCallMode(voiceComponent)

  const accent = normaliseHex(uic.organisationContext.lookAndFeel.assetFrontendBgcolorHex)
  const logoUrl = uic.organisationContext.lookAndFeel.logoUrl

  const phoneFor = (c: PublicAssetFrontendApi$AssetComponentDto) =>
    voiceComponent != null && c.id === voiceComponent.id && voiceCall.mode === 'instant_call'
      ? voiceCall.phone
      : null

  const isCall = (c: PublicAssetFrontendApi$AssetComponentDto) =>
    c.key === KEY.VideoCall || c.key === KEY.VoiceCall

  const callSubtitle = (c: PublicAssetFrontendApi$AssetComponentDto) =>
    c.key === KEY.VideoCall
      ? t('component.videocall.subtitlePrompt')
      : t('component.voicecall.subtitlePrompt')

  // The organisation's own description wins; the call prompts are the fallback.
  const subtitleFor = (c: PublicAssetFrontendApi$AssetComponentDto) =>
    c.description ?? (isCall(c) ? callSubtitle(c) : undefined)

  useEffect(() => {
    if (details != null) {
      if (details.inferredCategoryView?.uiNudgeToInputSerial === true) {
        if (nudgeDecision == false) {
          setMarkAsVisited(1)
        }
      } else {
        setMarkAsVisited(1)
      }
    }
  }, [details, nudgeDecision])

  // However the drawer closes, a handed-over question must not be re-sent the
  // next time the AI chat opens.
  useEffect(() => {
    if (facade.selectedComponent == null) {
      setAiQuestion(undefined)
    }
  }, [facade.selectedComponent])

  useEffect(() => {
    if (markAsVisited == 1 && details != null) {
      visitAsset(uic._orgId, details)
    }
  }, [uic._orgId, markAsVisited, details])

  if (facade.isRequestPending) {
    return <RdaLoading />
  }

  // Asset Protection hides this product from the visitor: no serial, product
  // name or components came back, so the page is only the way to the account.
  // `details` is null here, so the visit is never recorded. Home, Scan and
  // Account stay in the nav: nothing asset-specific, and a visitor who scanned
  // the wrong tag is not left at a dead end.
  if (facade.protectedSignInRequired) {
    return (
      <AssetShell
        accent={accent}
        nav={
          <PreviewNav
            active={
              facade.assetViewFunction === 'Scan'
                ? 'scan'
                : facade.assetViewFunction === 'Account' ||
                    facade.assetViewFunction === 'CreateAccount'
                  ? 'account'
                  : null
            }
            onAccount={() => facade.showAccountView()}
            onHome={() => {
              facade.hideViewFunctionOrDrawer()
              navigate('/')
            }}
            onScan={() => facade.showScanView()}
          />
        }
      >
        <PreviewHeader logoUrl={logoUrl} />
        <div className="px-4">
          <AssetProtectedSignIn onSignIn={() => facade.showAccountView()} />
        </div>
        <AccountDrawer
          close={() => facade.hideViewFunctionOrDrawer()}
          open={facade.assetViewFunction == 'Account' || facade.assetViewFunction == 'CreateAccount'}
          useThisFeatureAlert={facade.assetViewFunction == 'CreateAccount'}
        />
        <Scan close={() => facade.hideViewFunctionOrDrawer()} open={facade.assetViewFunction == 'Scan'} />
      </AssetShell>
    )
  }

  if (details == null || facade.assetViewOrAssetNotFound) {
    return (
      <AssetShell accent={accent}>
        <PreviewHeader logoUrl={logoUrl} />
        <div className="px-4 py-10">
          <h1 className="text-2xl font-semibold leading-tight tracking-[-0.02em]">Sorry</h1>
          <p className="mt-2 text-sm text-[var(--rda-muted)]">The asset could not be found.</p>
        </div>
      </AssetShell>
    )
  }

  const serial =
    details.inferredDefaultView?.serialNumber ??
    details.inferredSerialnumberPatternMatchView?.serialNumber ??
    ''
  const productName = details.assetProductName.trim()
  const nudgeOpen =
    nudgeDecision == null &&
    details.inferredCategoryView != null &&
    details.inferredCategoryView.uiNudgeToInputSerial
  const identity = details.inferredCategoryView
    ? {
        label: details.inferredCategoryView.uiTitle,
        title: details.inferredCategoryView.viewValue,
        subtitle: productName,
      }
    : {
        label: t('assetView.currentAsset'),
        title: serial || productName,
        subtitle: serial ? productName : '',
      }

  return (
    <AssetShell
      accent={accent}
      nav={
        facade.selectedComponent == null && !nudgeOpen ? (
          <PreviewNav
            // An asset page is not Home; only an open Scan or Account
            // drawer is a tab the visitor is on.
            active={
              facade.assetViewFunction === 'Scan'
                ? 'scan'
                : facade.assetViewFunction === 'Account' ||
                    facade.assetViewFunction === 'CreateAccount'
                  ? 'account'
                  : null
            }
            onAccount={() => facade.showAccountView()}
            onHome={() => {
              facade.hideViewFunctionOrDrawer()
              navigate('/')
            }}
            onScan={() => facade.showScanView()}
          />
        ) : undefined
      }
    >
      <PreviewHeader logoUrl={logoUrl} />

      {/* One column on the 16px gutter: every block shares the same left and
          right edge, and every block pads its content by the same 16px. */}
      <div className="flex flex-col gap-3 px-4">
        {/* The current asset, as in the reference: a small spaced label, the
            serial number big, the product name under it. A category view has
            no serial; its own title is the label and its value the big line. */}
        <div className="rounded-[10px] sm:rounded-2xl border border-[color:var(--rda-hairline)] bg-[var(--rda-surface)] px-4 pb-4 pt-3.5 sm:pb-5 sm:pt-4 text-[var(--rda-on-surface)]">
          <p className="text-[11px] font-semibold uppercase leading-none tracking-[0.16em] sm:text-[12px] text-[var(--rda-muted)]">
            {identity.label}
          </p>
          <h1 className="mt-1.5 text-[22px] font-bold leading-[1.05] tracking-[-0.03em] sm:mt-2.5 sm:text-[34px] [overflow-wrap:anywhere]">
            {identity.title}
          </h1>
          {identity.subtitle ? (
            <p className="mt-0.5 text-[15px] leading-snug text-[var(--rda-muted)] sm:mt-1.5 sm:text-xl">
              {identity.subtitle}
            </p>
          ) : null}
        </div>

        <AssetSearch
          aiSupport={aiSupport}
          components={all}
          urlParams={details.urlParamsForRequests}
          onAskAi={(question) => {
            if (aiSupport == null) return
            setAiQuestion(question)
            facade.selectComponent(aiSupport)
          }}
          onScan={() => facade.showScanView()}
        >
          {/* The three highest-priority components, as big boxes. */}
          {support.length > 0 ? (
            <div
              className={
                'grid gap-2 ' +
                (support.length === 3
                  ? 'grid-cols-3'
                  : support.length === 2
                    ? 'grid-cols-2'
                    : 'grid-cols-1')
              }
            >
              {support.map((c) => (
                <SupportCard
                  key={`s-${c.id}`}
                  component={c}
                  instantPhone={phoneFor(c)}
                  subtitle={subtitleFor(c)}
                  onClick={() => facade.selectComponent(c)}
                />
              ))}
            </div>
          ) : null}

          {/* The remaining actions (register, voice, ...) by priority. */}
          {actions.length > 0 ? (
            <RowGroup>
              {actions.map((c) => (
                <ResourceRow
                  key={`x-${c.id}`}
                  grouped
                  tinted
                  component={c}
                  instantPhone={phoneFor(c)}
                  size="lg"
                  subtitle={subtitleFor(c)}
                  onClick={() => facade.selectComponent(c)}
                />
              ))}
            </RowGroup>
          ) : null}

          {/* The remaining file components, by priority. */}
          {docs.length > 0 ? (
            <section aria-labelledby="asset-docs-heading" className="mt-2 flex flex-col gap-2">
              <SectionHeading id="asset-docs-heading">
                {t('assetView.section.documentation')}
              </SectionHeading>
              <RowGroup>
                {docs.map((c) => (
                  <ResourceRow
                    key={`m-${c.id}`}
                    grouped
                    tinted
                    component={c}
                    size="lg"
                    subtitle={subtitleFor(c)}
                    onClick={() => facade.selectComponent(c)}
                  />
                ))}
              </RowGroup>
            </section>
          ) : null}
        </AssetSearch>
      </div>

      <AccountDrawer
        close={() => facade.hideViewFunctionOrDrawer()}
        open={facade.assetViewFunction == 'Account' || facade.assetViewFunction == 'CreateAccount'}
        useThisFeatureAlert={facade.assetViewFunction == 'CreateAccount'}
      />

      <Scan
        close={() => facade.hideViewFunctionOrDrawer()}
        open={facade.assetViewFunction == 'Scan'}
      />

      <RdaDrawer
        wide
        bodyClass={
          facade.selectedComponent?.key == PublicAssetFrontendApi$AssetComponentDtoKey.VideoCall
            ? 'pl-1 pr-1 pt-1 pb-1'
            : ''
        }
        isOpen={facade.selectedComponent != null}
        placement="right"
        title={facade.selectedComponent?.name ?? ''}
        width={'100%'}
        onClose={() => facade.hideViewFunctionOrDrawer()}
        onRequestClose={() => facade.hideViewFunctionOrDrawer()}
      >
        {facade.selectedComponent != null && (
          <AssetDrawerScope accent={accent}>
            {facade.selectedComponent.filesystemComponent && (
              <FileComponent
                assetComponent={facade.selectedComponent}
                assetViewDetails={details}
                onSignIn={() => facade.showAccountView()}
              />
            )}

            {!facade.selectedComponent.filesystemComponent && (
              <>
                {facade.selectedComponent.key ==
                  PublicAssetFrontendApi$AssetComponentDtoKey.RegisterProduct && (
                  <RegisterProduct
                    assetView={details}
                    componentId={facade.selectedComponent.id}
                    onDone={() => facade.hideViewFunctionOrDrawer()}
                  />
                )}
                {facade.selectedComponent.key ==
                  PublicAssetFrontendApi$AssetComponentDtoKey.Inquiry && (
                  <Inquiry
                    assetView={details}
                    componentId={facade.selectedComponent.id}
                    onDone={() => facade.hideViewFunctionOrDrawer()}
                  />
                )}
                {facade.selectedComponent.key ==
                  PublicAssetFrontendApi$AssetComponentDtoKey.AiSupport && (
                  <AiSupportComponent assetViewDetails={details} initialQuestion={aiQuestion} />
                )}
                {facade.selectedComponent.key ==
                  PublicAssetFrontendApi$AssetComponentDtoKey.VideoCall && (
                  <VideoCallAppPageComponent
                    assetView={details}
                    component={facade.selectedComponent}
                    onUserRequestsCloseCall={facade.hideViewFunctionOrDrawer}
                  />
                )}
                {facade.selectedComponent.key ==
                  PublicAssetFrontendApi$AssetComponentDtoKey.VoiceCall && (
                  <VoiceCallAppPageComponent component={facade.selectedComponent} />
                )}
              </>
            )}
          </AssetDrawerScope>
        )}
      </RdaDrawer>

      <NudgeSerialSheet
        accent={accent}
        assetViewDetails={details}
        show={nudgeOpen}
        onDenyNudge={() => setNudgeDecision(false)}
      />
    </AssetShell>
  )
}
